/**
 * Client API pour CEE TRA-EQ-131 (dossiers + documents + events).
 *
 * S1 : listDossiers, getStats, getDossier
 * S2 : detail dossier (notes, validate/reject docs, upload, supersede, events,
 *      transmis, blocage)
 *
 * Inbox / reconcile separe dans `./ceeInbox.js` (taille).
 *
 * Note S2 : certains endpoints sont appeles avant que Track A/B ne les ait
 * deployes (mock-first). En cas d echec preprod, ce sera resolu a l integration.
 */

import { apiFetch } from './client.js';

// ---- Liste & stats (S1) ----

/**
 * Liste les dossiers CEE avec filtres optionnels.
 * @param {{ status?: string, mandataire?: string, search?: string, alerteOnly?: boolean }} [filters]
 * @returns {Promise<Array<object>>}
 */
export async function listDossiers(filters = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '' && v !== false) {
      qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch(`/cee/dossiers${suffix}`);
}

/**
 * Recupere les stats globales du pipeline CEE (4 cards + alertes).
 * @returns {Promise<{ total: number, collecte: number, pretSigner: number, termines: number, alertes: number, byStatus?: Record<string, number>, byMandataire?: Record<string, number> }>}
 */
export async function getStats() {
  return apiFetch('/cee/stats');
}

/**
 * Recupere un dossier CEE par id (avec client + mandataire + statuts docs).
 * @param {string} dossierId
 * @returns {Promise<object | null>}
 */
export async function getDossier(dossierId) {
  return apiFetch(`/cee/dossiers/${dossierId}`);
}

// ---- Mutations dossier (S2) ----

/**
 * Met a jour des champs editables du dossier (notes, status, blocking_reason).
 * @param {string} dossierId
 * @param {{ notes?: string, status?: string | null, blockingReason?: string | null }} patch
 * @returns {Promise<object>}
 */
export async function updateDossier(dossierId, patch) {
  return apiFetch(`/cee/dossiers/${dossierId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ---- Documents (S2) ----

/**
 * Marque un document comme valide.
 * @param {string} dossierId @param {string} docId @returns {Promise<object>}
 */
export async function validateDocument(dossierId, docId) {
  return apiFetch(`/cee/dossiers/${dossierId}/documents/${docId}/validate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Marque un document comme rejete avec raison.
 * @param {string} dossierId @param {string} docId @param {string} reason
 * @returns {Promise<object>}
 */
export async function rejectDocument(dossierId, docId, reason) {
  return apiFetch(`/cee/dossiers/${dossierId}/documents/${docId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Upload manuel d un document (multipart/form-data).
 * @param {string} dossierId
 * @param {string} docType - 'urssaf' | 'liasse_2059_e' | 'kbis' | 'cni' | 'devis' | 'ah' | 'autre'
 * @param {File} file
 * @returns {Promise<object>}
 */
export async function uploadDocument(dossierId, docType, file) {
  const form = new FormData();
  form.append('doc_type', docType);
  form.append('file', file);
  return apiFetch(`/cee/dossiers/${dossierId}/documents/upload`, {
    method: 'POST',
    body: form,
  });
}

/**
 * Marque un doc comme supersede (en attente du remplacement).
 * @param {string} dossierId @param {string} docId @returns {Promise<object>}
 */
export async function supersedeDocument(dossierId, docId) {
  return apiFetch(`/cee/dossiers/${dossierId}/documents/${docId}/supersede`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// downloadDocumentUrl + downloadDossierZipUrl : URLs pures, voir cee/lib/ceeUrls.js
export { downloadDocumentUrl, downloadDossierZipUrl } from '../cee/lib/ceeUrls.js';

/**
 * Telecharge le ZIP dossier complet via fetch authentifie (Bearer token)
 * et trigger le download client-side via Object URL.
 *
 * Codex P1 (commit 6f9666a) : un <a download href> n envoie PAS Authorization.
 * Codex P1 (commit 456a01d) : apiFetch parse automatiquement en JSON, donc
 *   il echoue sur du binaire ZIP. On utilise un raw fetch avec le Bearer
 *   token pour recuperer la Response avant .blob().
 *
 * @param {string|number} dossierId
 * @returns {Promise<void>}
 */
export async function downloadDossierZip(dossierId) {
  // localStorage key = 'token' (cf api/client.js setToken)
  const token = localStorage.getItem('token');
  const url = `/api/cee/dossiers/${dossierId}/download-zip`;
  const r = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) {
    throw new Error(`Echec telechargement ZIP (HTTP ${r.status})`);
  }
  const cd = r.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : `dossier-${dossierId}.zip`;
  const blob = await r.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

// ---- Generation Devis/AH + transmission (S3.3) ----

/**
 * Genere un Devis ou AH pour ce dossier (server merge depuis template).
 * @param {string} dossierId
 * @param {'devis' | 'ah'} type
 * @returns {Promise<{ documentId: number, downloadUrl: string }>}
 */
export async function generateDoc(dossierId, type) {
  return apiFetch(`/cee/dossiers/${dossierId}/generate`, {
    method: 'POST',
    body: JSON.stringify({ type }),
  });
}

/**
 * Marque le dossier comme transmis au mandataire (event SENT_TO_MANDATAIRE).
 * Recompute -> status passe a depot_cee si tous docs valides.
 * @param {string} dossierId
 * @returns {Promise<{ ok: true, status: string | null }>}
 */
export async function markSentToMandataire(dossierId) {
  return apiFetch(`/cee/dossiers/${dossierId}/sent-to-mandataire`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---- Events / Timeline (S2) ----

/**
 * Liste les events d un dossier (timeline).
 * @param {string} dossierId @param {number} [limit] @returns {Promise<Array<object>>}
 */
export async function getEvents(dossierId, limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) }).toString();
  return apiFetch(`/cee/dossiers/${dossierId}/events?${qs}`);
}

// ---- Parametres (S3.5) ----

/**
 * Liste des mandataires.
 * @returns {Promise<Array<object>>}
 */
export async function listMandataires() {
  return apiFetch('/cee/parametres/mandataires');
}

/**
 * Cree un mandataire.
 * @param {{ code: string, nom: string, delegataire?: string, contact_nom?: string, contact_email?: string, active?: boolean }} body
 * @returns {Promise<object>}
 */
export async function createMandataire(body) {
  return apiFetch('/cee/parametres/mandataires', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Met a jour un mandataire.
 * @param {number} id
 * @param {object} body
 * @returns {Promise<object>}
 */
export async function updateMandataire(id, body) {
  return apiFetch(`/cee/parametres/mandataires/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Supprime un mandataire (refuse si dossiers attaches).
 * @param {number} id
 * @returns {Promise<{ ok: true }>}
 */
export async function deleteMandataire(id) {
  return apiFetch(`/cee/parametres/mandataires/${id}`, { method: 'DELETE' });
}

/**
 * Liste les utilisateurs CEE de la campagne active.
 * @returns {Promise<Array<object>>}
 */
export async function listParametresUsers() {
  return apiFetch('/cee/parametres/users');
}

/**
 * Change role/mandataire d un utilisateur de la campagne active.
 * @param {string} userId
 * @param {{ role: string, mandataire_code?: string }} body
 */
export async function updateParametresUser(userId, body) {
  return apiFetch(`/cee/parametres/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Status des templates docx (devis/ah) + warnings.
 * @returns {Promise<{ devis: object, ah: object, warnings: Array<object> }>}
 */
export async function getTemplatesStatus() {
  return apiFetch('/cee/parametres/templates');
}

/**
 * Status de l ingest (singleton + 10 derniers events).
 * @returns {Promise<{ state: object | null, recentEvents: Array<object> }>}
 */
export async function getIngestStatus() {
  return apiFetch('/cee/parametres/ingest');
}

/**
 * Force un cycle d ingest manuellement (admin).
 * @returns {Promise<object>}
 */
export async function forceIngestRun() {
  return apiFetch('/cee/admin/ingest/run-now', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---- Inbox (re-export pour compat) ----
export {
  getInbox,
  affectAttachment,
  reclassifyAttachment,
  ignoreAttachment,
  inboxAttachmentPreviewUrl,
  searchDossiersAutocomplete,
} from './ceeInbox.js';
